/* eslint-disable max-len */
import React, { ReactNode } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { SocietyWebsiteBase } from '../SocietyWebsiteBase'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { societyWebsiteNameDescriptionSchema } from '../../../../forms/schemas/society_website_name_description'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { TextInput } from '../../../common/TextInput'
import { TextArea } from '../../../common/TextArea'
import { ToastType } from '../../../common/Toast/toast-type'
import { ISocietyWebsiteModel } from '../../../../interfaces/models/societies-websites.interfaces'
import { patchWebsite } from '../../../../api/society-websites'

interface SocietyWebsiteGeneralInformationProps {
  website: ISocietyWebsiteModel
}

export const SocietyWebsiteGeneralInformation = ({
  website,
}: SocietyWebsiteGeneralInformationProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { getErrorMessage } = useFormErrorMessage()

  const getDefaultValues = (): InferType<
    typeof societyWebsiteNameDescriptionSchema
  > => {
    return {
      displayName: website.displayName || '',
      description: website.description || '',
    }
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyWebsiteNameDescriptionSchema),
    defaultValues: getDefaultValues(),
  })

  const { mutate: mutatePatchWebsite, isPending: isUpdatingWebsite } =
    useMutation({
      mutationFn: patchWebsite,
      onSuccess: () => {
        setToastNotification(
          ToastType.SUCCESS,
          translate(
            'societyWebsiteGeneralInformation.flashMessage.updateSuccess'
          )
        )
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate(
            'societyWebsiteGeneralInformation.flashMessage.updateFailure'
          )
        )
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyWebsiteNameDescriptionSchema>
  ): Promise<void> => {
    mutatePatchWebsite({ id: website._id, body: data })
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('societyWebsiteGeneralInformation.flashMessage.updateFailure')
    )
  }

  const loading = isUpdatingWebsite

  return (
    <form
      className="w-full max-w-3xl"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <SocietyWebsiteBase website={website} loading={loading} isValid={isValid}>
        <div className="max-w-screen-sm space-y-6">
          <Controller
            control={control}
            name="displayName"
            render={({ field: { value, onChange, onBlur, name } }) => (
              <FormControl
                name={name}
                error={
                  errors.displayName && getErrorMessage(errors.displayName)
                }
                label={translate(
                  'societyWebsiteGeneralInformation.form.displayName'
                )}
              >
                <TextInput value={value} onChange={onChange} onBlur={onBlur} />
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field: { value, onChange, onBlur, name } }) => (
              <FormControl
                name={name}
                error={
                  errors.description && getErrorMessage(errors.description)
                }
                label={translate(
                  'societyWebsiteGeneralInformation.form.description'
                )}
              >
                <TextArea
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  rows={10}
                />
              </FormControl>
            )}
          />
        </div>
      </SocietyWebsiteBase>
    </form>
  )
}
